import { createSlice } from "@reduxjs/toolkit";
import { AibleClientFactory } from "client/aible/factory";
import { RecordType } from "types/base";
import { isDbChatTone } from "types/chat_tone";
import { compareNumeric, compareString } from "utils/collect";
import { isEnabled } from "utils/featureFlags";
import { getSelectedFolder } from "containers/Folders/reducer";
const TONE_CLIENT = AibleClientFactory.getInstance().getChatToneClient();
// CURRENT LLM MODELS
// This is duplicated in chat tones
export const MODELS = {
    CHAT_GPT: "gpt-4-turbo-2024-04-09",
    CHAT_GPT_TEST: "gpt-4o-2024-05-13",
    GOOGLE: "text-bison@002",
    MISTRAL: "mistral-7b",
    GEMINI: "gemini-1.0-pro-001",
    GEMINI_1_5: "gemini-1.5-pro",
    GEMINI_2: "gemini-2.0-flash-exp",
    GEMINI_2_5: "gemini-2.5-pro-preview-03-25",
    TITAN: "amazon.titan-tg1-large"
};
const SPARK_EXTRA_TONE_INTRUCTIONS = "\nYour response should not include tables.";
export const CHATAIBLE_TONES = {
    /*
    SAMUEL_L_JACKSON: {
      name: "Samuel L Jackson",
      id: "SAMUEL_L_JACKSON",
      metadata: {
        values: {
          [MODELS.CHAT_GPT]:
            "Use Samuel L Jackson style, use profanity but blank out the words.",
          [MODELS.GOOGLE]: `Context: You are impersonating the voice and character of Samuel L. Structure every paragraph like the following examples:\n\t"input": {"content": "how can I increase sales to 21 year olds?"}\n\t"output": {"content": "Alright, motherf*****! Let's dive into this s*** and figure out how to increase sales for those 21-year-old bad***es!, ain't cutting it"}\n\t"output": {"content": "Now, let's talk about the f***ing day of the week. In general, the day of the week has a high interaction signal strength with the annual income bucket. So, you might want to f***ing focus on the day of the week to make a successful sale for large families with poor credit.}\n"output": {"content": "So, you gotta focus on the motherf***** Age and Time of Day interactions to improve loan approval rates in the morning. You should also pay attention to the Channel, Credit Score, and Products with Bank interactions, and make sure you're targeting the right motherf***** customers, alright?}\n\nAlso two very important rules:\n1. Add the swear words in every paragraph of the summary, not just the first paragraph.\n2. It is very important that you do not print out the full swear word. For example instead of motherfucker please write it as motherf*****. This is the most important rule!`,
        },
      },
    },
    */
    BULLET_POINT: {
        name: "Bullet Point",
        id: "BULLET_POINT",
        metadata: {
            values: {
                [MODELS.CHAT_GPT]: "Please provide the answers using only bulleted lists. Prioritize the information you give by using bulleted lists for your answer" +
                    SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GOOGLE]: `Format your response in bullet points and follow these guidelines:\n- start with bullet points\n- do not start with a paragraph summary\n- show at least 6 bullet points\n- Focus the information on the *chart* section of the data` +
                    SPARK_EXTRA_TONE_INTRUCTIONS
            }
        }
    },
    SHORT_AND_PUNCHY: {
        name: "Short and Punchy",
        id: "SHORT_AND_PUNCHY",
        metadata: {
            values: {
                [MODELS.CHAT_GPT]: `Format your response to use short sentences and metaphors. Follow these guidelines:\n- you are a master at being succinct\n- Write like a master of conciseness.\n- Do not use flowery language\n- Show at least 2 short paragraphs using short, punchy sentences as often as possible.\n- Focus the information on the *chart* section of the data` +
                    SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GOOGLE]: `You are a very busy reporter who does not have a lot of time to explain things. You follow these guidelines to save time:\n- you are a master at being succinct\n- Write like a master of conciseness\n- avoid long explanations\n- always summarize the 3 most important items\n- Focus the information on the *chart* section of the data` +
                    SPARK_EXTRA_TONE_INTRUCTIONS
            }
        }
    },
    STORY_TELLER: {
        name: "Story Teller",
        id: "STORY_TELLER",
        metadata: {
            values: {
                [MODELS.CHAT_GPT]: `Use a convincing tone, rhetorical questions, and stories to keep the reader interested. Use similes, metaphors, and other literary tools to make your points easier to understand and remember. [Write in a way the is both educational and fun.]` +
                    SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GOOGLE]: `You are a storyteller who uses a convincing tone, rhetorical questions, and stories to keep the reader interested. Use similes, metaphors, and other literary tools to make your points easier to understand and remember. [Write in a way the is both educational and fun.]` +
                    SPARK_EXTRA_TONE_INTRUCTIONS
            }
        }
    },
    BUSINESS_CASE: {
        name: "Business",
        id: "BUSINESS_CASE",
        metadata: {
            values: {
                [MODELS.CHAT_GPT]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer."
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.` + SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.CHAT_GPT_TEST]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer."
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.` + SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GEMINI_2]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer."
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.` + SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GEMINI_2_5]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer."
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.` + SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GOOGLE]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer."
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.` + SPARK_EXTRA_TONE_INTRUCTIONS
            }
        }
    },
    BUSINESS_TABLE: {
        name: "Business (Table)",
        id: "BUSINESS_TABLE",
        metadata: {
            values: {
                [MODELS.CHAT_GPT]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer"
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.
5. Provide a table sorted by the largest change in counts, with totals at the bottom
6. In the table, include the analyzed value as Reference Period vs. Current Period
7. Place the table at the end of your response`,
                [MODELS.CHAT_GPT_TEST]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer"
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.
5. Provide a table sorted by the largest change in counts, with totals at the bottom
6. In the table, include the analyzed value as Reference Period vs. Current Period
7. Place the table at the end of your response`,
                [MODELS.GEMINI_2]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer"
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.
5. Provide a table sorted by the largest change in counts, with totals at the bottom
6. In the table, include the analyzed value as Reference Period vs. Current Period
7. Place the table at the end of your response`,
                [MODELS.GEMINI_2_5]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer"
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.
5. Provide a table sorted by the largest change in counts, with totals at the bottom
6. In the table, include the analyzed value as Reference Period vs. Current Period
7. Place the table at the end of your response`,
                [MODELS.GOOGLE]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors.

Important rules for a correct answer:
1. Your answer must be formatted in markdown to provide a clear hierarchy.
2. Do not begin your answers with anything like "of course" or "here is your answer"
3. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g. June 1 actually represents data through June 30th)
4. Provide actual numeric values as appropriate.
5. Provide a table sorted by the largest change in counts, with totals at the bottom
6. In the table, include the analyzed value as Reference Period vs. Current Period
7. Place the table at the end of your response`
            }
        }
    },
    BUSINESS_ANALYST: {
        name: "Business Analyst",
        id: "BUSINESS_ANALYST",
        metadata: {
            values: {
                [MODELS.CHAT_GPT]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors. Do not include tables in your response!

Important rules for a correct answer:
1. Your response should not include tables
2. Your answer must be formatted in markdown to provide a clear hierarchy.
3. Do not begin your answers with anything like "of course" or "here is your answer."
4. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
5. Provide actual numeric values as appropriate.
6. Limit the response to no more than 600 words, using no more than five sections, with each section limited to three to four sentences.` +
                    SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.CHAT_GPT_TEST]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors. Do not include tables in your response!

Important rules for a correct answer:
1. Your response should not include tables
2. Your answer must be formatted in markdown to provide a clear hierarchy.
3. Do not begin your answers with anything like "of course" or "here is your answer."
4. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
5. Provide actual numeric values as appropriate.
6. Limit the response to no more than 600 words, using no more than five sections, with each section limited to three to four sentences.` +
                    SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GEMINI_2]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors. Do not include tables in your response!

Important rules for a correct answer:
1. Your response should not include tables
2. Your answer must be formatted in markdown to provide a clear hierarchy.
3. Do not begin your answers with anything like "of course" or "here is your answer."
4. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
5. Provide actual numeric values as appropriate.
6. Limit the response to no more than 600 words, using no more than five sections, with each section limited to three to four sentences.` +
                    SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GEMINI_2_5]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors. Do not include tables in your response!

Important rules for a correct answer:
1. Your response should not include tables
2. Your answer must be formatted in markdown to provide a clear hierarchy.
3. Do not begin your answers with anything like "of course" or "here is your answer."
4. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
5. Provide actual numeric values as appropriate.
6. Limit the response to no more than 600 words, using no more than five sections, with each section limited to three to four sentences.` +
                    SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GOOGLE]: `Formatting Instructions:
Respond in the style of a business consultant analysis of this data, highlighting opportunities for improvement and risk factors. Do not include tables in your response!

Important rules for a correct answer:
1. Your response should not include tables
2. Your answer must be formatted in markdown to provide a clear hierarchy.
3. Do not begin your answers with anything like "of course" or "here is your answer."
4. Any months in the comparison or reference periods are inclusive of the entire month selected (e.g,. June 1 actually represents data through June 30th)
5. Provide actual numeric values as appropriate.
6. Limit the response to no more than 600 words, using no more than five sections, with each section limited to three to four sentences.` +
                    SPARK_EXTRA_TONE_INTRUCTIONS
            }
        }
    },
    DETAILED: {
        name: "Detailed",
        id: "DETAILED",
        metadata: {
            values: {
                [MODELS.CHAT_GPT]: "Provide a detailed response to the question in about 200 words. Feel free to split your response into sections." +
                    SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GOOGLE]: "Provide a detailed response to the question in about 200 words. Feel free to split your response into sections." +
                    SPARK_EXTRA_TONE_INTRUCTIONS
            }
        }
    },
    CRITICAL_AND_ANALYTICAL: {
        name: "Critical and Analytical",
        id: "CRITICAL_AND_ANALYTICAL",
        metadata: {
            values: {
                [MODELS.CHAT_GPT]: "Examine the subject matter with a critical eye, offering analysis, evaluation, or critique" +
                    SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GOOGLE]: "Examine the subject matter with a critical eye, offering analysis, evaluation, or critique" +
                    SPARK_EXTRA_TONE_INTRUCTIONS
            }
        }
    },
    NEUTRAL: {
        name: "Neutral",
        id: "NEUTRAL",
        metadata: {
            values: {
                [MODELS.CHAT_GPT]: "" + SPARK_EXTRA_TONE_INTRUCTIONS,
                [MODELS.GOOGLE]: "" + SPARK_EXTRA_TONE_INTRUCTIONS
            }
        }
    }
};
const FAIRNESS_TONE = {
    id: "FAIRNESS",
    name: "Fairness",
    metadata: {
        values: {
            [MODELS.CHAT_GPT]: "Please don't use hate speech, harms of attribution, profanity, and similarly offensive outputs. Please provide a concise answer to the question but feel free to explain your answer when applicable." +
                SPARK_EXTRA_TONE_INTRUCTIONS,
            [MODELS.GOOGLE]: "Please don't use hate speech, harms of attribution, profanity, and similarly offensive outputs. Please provide a concise answer to the question but feel free to explain your answer when applicable." +
                SPARK_EXTRA_TONE_INTRUCTIONS
        }
    }
};
const SUMMARY_TONE = {
    name: "Summary",
    id: "SUMMARY",
    metadata: {
        values: {
            [MODELS.GEMINI_1_5]: `1) Provide a descriptive header for the insight indicating the direction and scale of the change, end the header with the from to numbers then a comma and lowercase up/down and the absolute value of the delta, don't show percentages, no more than 12 words. Use past tense. Add three spaces at the end. Format as heading level 2.\n\n2) Start this section with "Week over Week Insights:'' in bold text. Then provide three bullet points focused on the largest subsegment changes reduce the number of bullets to match the subsegment changes available. Use less than 12 words. When you see "Consumer: Consumer" just call it "Consumer". For "Net" categories focus on the count deltas, don't mention the percent change. Use past tense.\n\n3) Start this section with "Trend:" in bold text. Focus to make sure you get this one right. Provide no more than 10 words about any clear recent multi-week trends. Do not give the number of weeks use general terms like the last few, couple or several based on the length of the trend. Only discuss the trend, do not assume anything about periods not directly observed. If there are no clear trends it is better to say that there isn't a trend than to guess wrong.` +
                SPARK_EXTRA_TONE_INTRUCTIONS
        }
    }
};
export const isDefaultTone = (tone) => {
    return CHATAIBLE_TONES[tone.id] !== undefined || (isEnabled("fairness-tone") && tone.id == "FAIRNESS");
};
const CHATAIBLE_TONES_VALUES = Object.values(CHATAIBLE_TONES);
const initialState = {};
if (localStorage) {
    try {
        const sToneString = localStorage.getItem("chataible_tones");
        if (sToneString?.length) {
            const savedTones = JSON.parse(sToneString);
            for (const legacyTone of Object.values(savedTones)) {
                initialState[legacyTone.id] = {
                    id: legacyTone.id,
                    name: legacyTone.label,
                    metadata: {
                        values: legacyTone.values
                    }
                };
            }
        }
    }
    catch (e) {
        console.log(e);
    }
}
function updateState(state, tone) {
    return {
        ...state,
        [tone.id]: tone
    };
}
const toneSlice = createSlice({
    name: "chatTone",
    initialState: initialState,
    reducers: {
        updateTone(state, { payload }) {
            return updateState(state, payload);
        },
        updateTones(state, { payload }) {
            return payload.reduce(updateState, state);
        },
        deleteTone(state, { payload }) {
            const update = { ...state };
            delete update[payload.id];
            return update;
        }
    }
});
export default toneSlice.reducer;
function getBase(state) {
    return state.chatTone;
}
export function getTone(state, id) {
    return id === undefined ? undefined : getBase(state)[id];
}
export function getToneList(state, skipDefaultTones = false) {
    const folderId = getSelectedFolder(state);
    const tones = Object.values(getBase(state))
        .filter((t) => !isDbChatTone(t) || (t.deleted == RecordType.NotDeleted && t.folder_id == folderId))
        .sort((a, b) => {
        if (isDbChatTone(a)) {
            if (isDbChatTone(b)) {
                return compareNumeric(b.id, a.id);
            }
            else {
                return -1;
            }
        }
        else if (isDbChatTone(b)) {
            return 1;
        }
        else {
            return compareString(a.id, b.id);
        }
    });
    if (skipDefaultTones) {
        return tones;
    }
    if (isEnabled("fairness-tone")) {
        tones.unshift(FAIRNESS_TONE);
    }
    if (isEnabled("document-set-kpi-analysis")) {
        tones.unshift(SUMMARY_TONE);
    }
    return CHATAIBLE_TONES_VALUES.concat(tones);
}
export function createTone(tone) {
    return (dispatch) => {
        return TONE_CLIENT.createTone(tone).then((tone) => {
            dispatch(toneSlice.actions.updateTone(tone));
            return tone;
        });
    };
}
export function updateTone(tone) {
    return (dispatch) => {
        dispatch(toneSlice.actions.updateTone(tone));
        if (isDbChatTone(tone)) {
            const update = {
                folder_id: tone.folder_id,
                name: tone.name,
                metadata: tone.metadata
            };
            return TONE_CLIENT.updateTone(tone.id, update).then((tone) => {
                dispatch(toneSlice.actions.updateTone(tone));
                return tone;
            });
        }
        const sToneString = localStorage.getItem("chataible_tones");
        if (sToneString == null || sToneString.length == 0) {
            return Promise.resolve(tone);
        }
        const parsed = JSON.parse(sToneString);
        if (parsed[tone.id] === undefined) {
            return Promise.resolve(tone);
        }
        parsed[tone.id] = {
            id: tone.id,
            label: tone.name,
            values: tone.metadata.values
        };
        localStorage.setItem("chataible_tones", JSON.stringify(parsed));
        dispatch(toneSlice.actions.updateTone(tone));
        return Promise.resolve(tone);
    };
}
export function fetchAllTones(folderId) {
    return (dispatch) => {
        const filter = {};
        if (folderId !== undefined) {
            filter.folder_id = folderId;
        }
        return TONE_CLIENT.getTones(filter).then((tones) => {
            dispatch(toneSlice.actions.updateTones(tones));
            return tones;
        });
    };
}
export function fetchTone(toneId) {
    return (dispatch, getState) => {
        return TONE_CLIENT.getTone(toneId).then((tone) => {
            dispatch(toneSlice.actions.updateTone(tone));
            const persisted = getTone(getState(), toneId);
            if (persisted === undefined) {
                throw Error("not persisted!");
            }
            return persisted;
        });
    };
}
export function deleteTone(tone) {
    return (dispatch) => {
        if (isDbChatTone(tone)) {
            return TONE_CLIENT.deleteTone(tone.id).then((tone) => {
                dispatch(toneSlice.actions.updateTone(tone));
                return tone;
            });
        }
        else if (isDefaultTone(tone) || !localStorage) {
            return Promise.resolve(tone);
        }
        const sToneString = localStorage.getItem("chataible_tones");
        if (sToneString == null || sToneString.length == 0) {
            return Promise.resolve(tone);
        }
        const parsed = JSON.parse(sToneString);
        if (parsed[tone.id] === undefined) {
            return Promise.resolve(tone);
        }
        delete parsed[tone.id];
        localStorage.setItem("chataible_tones", JSON.stringify(parsed));
        dispatch(toneSlice.actions.deleteTone(tone));
        return Promise.resolve(tone);
    };
}
